"use client";

import * as React from "react";
// import { iconList } from "@/assets/icons"
import { DayPicker } from "react-day-picker";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/atoms/button";
import { DayContent, IconLeft, IconRight } from "../calendor-sub-atom";
/**
 * Calendar Component
 *
 * A styled wrapper around `react-day-picker` with custom UI integration,
 * responsive design, and theme-based color variables.
 *
 * Supports range selection, outside days, and icon-based navigation.
 */
function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(className)}
      classNames={{
        multiple_months: "w-fit",
        months: "flex flex-col md:flex-row w-full gap-4 md:gap-0",
        month: "flex flex-col gap-1 md:gap-1.5 w-full",
        caption:
          "flex justify-center border-b border-b-[var(--stroke-gray-light)] pb-1 md:pb-2 pt-1 md:pt-4 px-2 md:px-4 relative items-center w-full text-[var(--text-gray-stronger)]",
        caption_label: "text-sm font-[var(--font-medium)] uppercase",
        nav: "flex items-center gap-1 ",
        nav_button_previous: "absolute left-1 cursor-pointer",
        nav_button_next: "absolute right-1 cursor-pointer",
        table: "w-full border-collapse space-x-1",
        head_row: "flex px-2 md:px-4 pb-0.5 md:pb-1 gap-2 md:gap-0",
        head_cell:
          "text-xs font-[var(--font-medium)] text-[var(--text-gray-light)] flex items-center justify-center h-5 md:h-9 w-7 md:w-9",
        row: "flex w-full mt-0.5 md:mt-2 px-2 md:px-4 justify-between gap-2 md:gap-0 items-center !h-7 md:!h-9",
        cell: cn(
          "relative p-0 text-center text-xs focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[var(--fill-base-main)] rounded-full flex-1 !aspect-square flex items-center justify-center",
        ),
        day: cn(
          buttonVariants({ hierarchy: "ghost" }),
          "h-7 w-7 md:h-9 md:w-9 p-0 font-[var(--font-regular)] rounded-full text-[var(--text-gray-stronger)]",
        ),
        day_range_start:
          "day-range-start aria-selected:!bg-[var(--fill-base-main)] aria-selected:!text-white",
        day_range_end:
          "day-range-end aria-selected:!bg-[var(--fill-base-main)] aria-selected:!text-white",
        day_selected:
          "!h-7 !w-7 md:!h-9 md:!w-9 !rounded-full !bg-[var(--fill-base-main)] !text-white hover:!bg-[var(--fill-primary-solid-strongest)] focus:bg-primary focus:text-primary-foreground",
        day_today:
          " border border-[var(--fill-base-main)] rounded-full text-accent-foreground",
        day_outside:
          "day-outside !text-[var(--text-gray-light)] aria-selected:text-muted-foreground",
        day_disabled: "text-[var(--text-gray-light)] opacity-50",
        day_range_middle:
          "aria-selected:!bg-[var(--fill-base-main)] aria-selected:text-white",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft,
        IconRight,
        DayContent,
        Footer:
          props.mode === "range"
            ? (_) => props.footer as JSX.Element
            : (_) => null,
      }}
      {...props}
    />
  );
}

export { Calendar };
